/**
 * Turning sample COUNTS into DISTANCES, safely.
 *
 * SI is the loss payload's spatial sampling interval — metres per sample — and
 * every length on the fibre-health chart is `points * SI`. It is also the one
 * field nothing validated: a payload that omits it, or sends 0, produced NaN
 * lengths, and a NaN axis range makes SciChart throw. The throw then left the
 * zoom latch armed, and an armed latch costs a full-resolution GetLossData for a
 * zoom the user never performed — so one missing field became a wasted refetch on
 * every load.
 *
 * Reproduced from a malformed payload; a server that stopped sending SI would do
 * the same in production.
 *
 * PURE AND SEPARATE so the rule can be tested without SciChart, a component or a
 * telemetry stream — which is the only way this gets tested at all.
 */

/**
 * The usable sampling interval, or null when there is not one.
 *
 * Null rather than a fallback NUMBER, deliberately. A substituted interval would
 * draw a chart with a silently wrong distance axis, and a fibre-health chart whose
 * x-axis lies about WHERE a fault is, is worse than one that declines to redraw:
 * the operator digs in the wrong place. Callers render what they can and leave the
 * axis alone.
 */
export function usableSamplingInterval(si: unknown): number | null {
  if (typeof si !== 'number') return null;
  if (!Number.isFinite(si)) return null;
  if (si <= 0) return null;
  return si;
}

/**
 * `points * SI`, or null if either input is unusable.
 *
 * A negative point count is also refused: it means the caller's start/end indices
 * are crossed, and multiplying them yields a negative "length" that reverses an
 * axis rather than failing.
 */
export function spatialLength(points: unknown, si: unknown): number | null {
  const interval = usableSamplingInterval(si);
  if (interval === null) return null;
  if (typeof points !== 'number' || !Number.isFinite(points) || points < 0) return null;
  return points * interval;
}

/** True when this payload can be drawn on a distance axis at all. */
export function hasUsableScale(lossData: { SI?: unknown } | null | undefined): boolean {
  return usableSamplingInterval(lossData?.SI) !== null;
}

/**
 * The curing chart's samples-to-distance factor, or null when there isn't one.
 *
 * `xFactor = (CRE - CRB) / (LES - LSS)` converts a sample index into a position
 * along the liner. Both halves can be zero in a real payload: beta-dev returns
 * `SI/MI/CRB/CRE/CRS/LSS/LES/CT/OT` all 0 for finished projects, so the division
 * is 0/0 = NaN, every x value is NaN, and SciChart throws
 * "Argument min must be a real number" while measuring the axis. The chart then
 * renders nothing and the operator is told nothing.
 *
 * Measured on beta-dev 2026-09-06, project1_2025-03-06_00-12-13: 768 rows, each
 * carrying 1062 real temperatures in TR. THE DATA IS INTACT — only the geometry
 * describing where along the liner those readings sit is missing. That is why
 * this returns null instead of substituting 1: a fabricated factor would draw a
 * confident curve against a distance axis that is pure invention, and this chart
 * is read to decide where a cure is under-heating. A wrong position is worse
 * than a stated absence, exactly as for SI above.
 *
 * The numerator is refused when zero for the same reason it is refused when the
 * denominator is: every sample would map to the same position, collapsing 1062
 * readings onto one x and drawing a vertical line that means nothing.
 */
export function usableXFactor(
  crb: unknown,
  cre: unknown,
  lss: unknown,
  les: unknown
): number | null {
  const nums = [crb, cre, lss, les];
  if (nums.some((n) => typeof n !== 'number' || !Number.isFinite(n))) return null;
  const span = (cre as number) - (crb as number);
  const samples = (les as number) - (lss as number);
  if (samples <= 0) return null;
  if (span <= 0) return null;
  const factor = span / samples;
  return Number.isFinite(factor) ? factor : null;
}

/**
 * True when a temperature payload carries enough geometry to plot a distance axis.
 *
 * Separate from `hasUsableScale` because the two charts are scaled by different
 * fields — loss by SI, temperature by the CR/LS pair — and conflating them would
 * make one chart's missing field blank the other.
 */
export function hasUsableCuringGeometry(
  temp: { CRB?: unknown; CRE?: unknown; LSS?: unknown; LES?: unknown } | null | undefined
): boolean {
  return usableXFactor(temp?.CRB, temp?.CRE, temp?.LSS, temp?.LES) !== null;
}
